import { useCallback, useEffect } from "react";
import { useBlocker } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
} from "@components/ui/dialog";
import { Button } from "@components/ui/button";
import { Loader } from "lucide-react";

interface UseUnsavedChangesReturn {
  showDialog: boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
}

interface UnsavedChangesDialogProps {
  open: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export function useUnsavedChanges(isDirty: boolean): UseUnsavedChangesReturn {
  // Block in-app navigation (sidebar, links, back button) when dirty
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: any) =>
        isDirty && currentLocation.pathname !== nextLocation.pathname,
      [isDirty],
    ),
  );

  // Block tab close / refresh
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // ← NEW: intercept browser back button via History API
  useEffect(() => {
    if (!isDirty) return;

    // Push a dummy state so there's something to "go back" from
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      // Push again to prevent actual navigation
      window.history.pushState(null, "", window.location.href);
      // Trigger the blocker manually by dispatching a custom event
      // The blocker will pick this up since we push state and React Router re-evaluates
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isDirty]);

  const showDialog = blocker.state === "blocked";

  const confirmNavigation = useCallback(() => {
    if (blocker.state === "blocked") blocker.proceed();
  }, [blocker]);

  const cancelNavigation = useCallback(() => {
    if (blocker.state === "blocked") blocker.reset();
  }, [blocker]);

  return { showDialog, confirmNavigation, cancelNavigation };
}

export function UnsavedChangesDialog({
  open,
  onSave,
  onDiscard,
  onCancel,
  isSaving = false,
}: UnsavedChangesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-[400px] pt-6 bg-background border border-border shadow-xl">
        <DialogHeader>
          <DialogDescription className="text-foreground text-sm">
            You have unsaved changes. Do you want to save before proceeding?
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={onDiscard} disabled={isSaving}>
            Discard Changes
          </Button>
          <Button variant="destructive" onClick={onSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader size={18} className="animate-spin mr-1" /> Saving…
              </>
            ) : (
              "Save and Continue"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
